import { useState, useEffect } from "react";
import { useDelete, usePagination, useSearchQuery } from "../../../hooks";
import { useReportWebSocket } from "../../../hooks/UseReportWebSocket";
import { createReportUpdateHandler } from "../../../hooks/UseReportWebSocket/reportUpdateUtils";
import { MPSharedHeader, MPSharedTable, MPSharedDeleteModal } from "../Shared";
import { getLocalizedString } from "../../../shared/translation";
import { ReportsForm } from "./form";
import {
  useGetResellerMonthlyReportsQuery,
  useDeleteResellerMonthlyReportMutation,
} from "../services";
import { capitalize, capitalizeAllFirstLetters } from "../../Tracking/utils";
import { utcToLocalGlobal } from "../../../shared/utility";
import { FMDPStorage } from "../../../shared/helper";
import sprite from "../../../assets/icons.svg";
import { SvgIcon } from "../../Shared";
import { QISpinner } from "../../../components";
import { toast } from "react-toastify";
import { executePDFDownload } from "../../Tracking/Reports/pdfDownloadService";

export const ReportsContainer = () => {
  const { page, setPage, perPage, pagination } = usePagination();

  const { setSimpleSearch, q } = useSearchQuery({
    page,
    setPage,
    simpleSearchKey: "name_cont",
  });

  const currentUser = FMDPStorage.get("current-user");

  const [idToEdit, setIdToEdit] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [setIsNew] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);

  const {
    data = { data: [], total_count: 0 },
    isFetching,
    isSuccess,
    error,
  } = useGetResellerMonthlyReportsQuery({
    page,
    per_page: perPage,
    order_by: "created_at",
    order_dir: "desc",
    q,
    //filter_user_id: getUserId(),
  });
  const {
    deleteItem: deleteReport,
    deleteErrorMsg,
    setDeleteErrorMsg,
  } = useDelete({
    deleteMutation: useDeleteResellerMonthlyReportMutation,
    closeModal: () => setShowDeleteWarning(false),
  });

  // Local state for reports to enable real-time updates
  const [localReports, setLocalReports] = useState(data?.data || []);

  // Update local reports when data changes from API (only when isFetching changes to false)
  useEffect(() => {
    if (!isFetching && data?.data && Array.isArray(data.data)) {
      setLocalReports(data.data);
    }
  }, [isFetching, data?.data]);

  // WebSocket integration for real-time report status updates
  const handleReportUpdate = async (reportData) => {
    if (!reportData) return;

    // Use functional state update to ensure we work with latest state
    setLocalReports(createReportUpdateHandler(reportData));
  };

  // Initialize WebSocket connection
  const { connectionStatus } = useReportWebSocket(
    currentUser?.id,
    handleReportUpdate
  );

  // Connection status indicator for development/debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('[ManagementPortal Reports] WebSocket Status:', connectionStatus);
  }

  // PDF Download state and handler
  const [pdfLoading, setPdfLoading] = useState({});

  const handlePDFDownload = (report) => {
    if (pdfLoading[report.id]) return;

    setPdfLoading((prev) => ({ ...prev, [report.id]: true }));

    executePDFDownload(report, {
      onLoadingStart: () => {},
      onLoadingEnd: () => {
        setPdfLoading((prev) => ({ ...prev, [report.id]: false }));
      },
      onError: (errorMessage) => {
        toast.error(errorMessage || "PDF generation failed", { autoClose: 5000, closeOnClick: true });
      },
      onUploadError: () => {
        toast.error("PDF upload failed", { autoClose: 5000, closeOnClick: true });
      },
    });
  };

  const headers = [
    {
      label: getLocalizedString("name", "Name"),
      key: "name",
      className: "name",
    },
    {
      label: getLocalizedString("report_type", "Report Type"),
      id: 2,
      className: "report-type",
      type: "component",
      component: ({ data }) => {
        return <span> {capitalize(data?.report_type)} </span>;
      },
    },
    {
      label: getLocalizedString("subtype", "Sub-Type"),
      className: "report-sub-type",
      type: "component",
      component: ({ data }) => {
        return <span> {capitalize(data?.report_sub_type)} </span>;
      },
    },
    {
      label: getLocalizedString("summary_interval", "Sumary Interval"),
      className: "report-sub-type",
      type: "component",
      component: ({ data }) => {
        return <span> {capitalize(data?.summary_interval)} </span>;
      },
    },
    {
      label: getLocalizedString("status", "Status"),
      id: 7,
      nestedValue: true,
      getNestedValue: ({ status }) => {
        switch (status) {
          case "in_progress":
            status = "In Progress";
            break;
          case "pending":
            status = "Scheduled";
            break;
          case "failure":
            status = "Failed";
            break;
          default:
            break;
        }
        status = status.replace("_", " ");
        return <h1 title={status}>{capitalize(status)}</h1>;
      },
    },
    {
      label: getLocalizedString("created_by", "Created By"),
      id: 8,
      key: "created_by",
      className: "added-by",
    },
    {
      label: getLocalizedString("created_at", "Created At"),
      id: 9,
      nestedValue: true,
      getNestedValue: ({ created_at }) => {
        return <h1 title={utcToLocalGlobal(created_at)}>{utcToLocalGlobal(created_at)}</h1>;
      },
    },
    {
      label: getLocalizedString("download", "Download"),
      id: 10,
      className: "text-center",
      nestedValue: true,
      getNestedValue: ({ report_file, status, id, name, report_type, created_at }) => {
        let statusIcon;
        switch (status) {
          case "in_progress":
            statusIcon = "time";
            break;
          case "pending":
            statusIcon = "time";
            break;
          case "failure":
            statusIcon = "cross";
            break;
          case "completed":
            statusIcon = "tick";
            break;
          default:
            break;
        }
        return (
          <div style={{ display: "flex", gap: "10px", justifyContent: "center", alignItems: "center" }}>
            {report_file ? (
              <>
                <a href={report_file} className="download-status">
                  <SvgIcon name="download" wrapperClass="icon-download" svgClass="icon" />
                </a>
                {status === "completed" && (
                  pdfLoading[id] ? (
                    <span className="pdf-loading-spinner" title="Generating PDF...">
                      <QISpinner size="18px" />
                    </span>
                  ) : (
                    <button
                      className="pdf-download-btn"
                      title="Download PDF"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                      onClick={() => handlePDFDownload({ id, name, report_type, created_at, report_file_url: report_file })}
                    >
                      <SvgIcon
                        name="pdf-download"
                        title="Download PDF"
                        wrapperClass="icon-pdf-download"
                        svgClass="icon"
                      />
                    </button>
                  )
                )}
              </>
            ) : (
              <span
                className="download-status status-in-progress disabled-button"
                title={capitalizeAllFirstLetters(status)}
              >
                <svg className="icon">
                  <use href={`${sprite}#${statusIcon}`}></use>
                </svg>
              </span>
            )}
          </div>
        );
      },
    },
  ];

  const createNewHandler = () => {
    setShowForm(true);
  };

  const deleteClickHandler = (datum) => {
    setShowDeleteWarning(true);
    setIdToDelete(datum.id);
  };

  const getDeleteItemName = (id) => {
    let name = "";
    if (localReports && Array.isArray(localReports)) {
      const index = localReports.findIndex((datum) => datum.id === id);
      if (index > -1) {
        name = localReports[index].name;
      }
    }
    return name;
  };

  return (
    <>
      <article className="main-container">
        <MPSharedHeader
          resourceName="Report"
          createNewHandler={createNewHandler}
          groupName="Reports"
          addNewLabel={getLocalizedString("generate_report", "Generate Report")}
          heading={getLocalizedString("reports", "Reports")}
          handleSearchKey={setSimpleSearch}
          simpleSearchPlaceholder={getLocalizedString("search_by_name", "Search by Name")}
          skipCanCreate={true}
        />
        <MPSharedTable
          resourceName="Report Configurations"
          isLoading={isFetching}
          error={error}
          isSuccess={isSuccess}
          headers={headers}
          data={localReports}
          pagination={{ ...pagination, count: data.total_count }}
          onDelete={deleteClickHandler}
          className="report"
          auditKey=""
          skipCanDelete={true}
          skipClientId
        />
      </article>

      {showForm && <ReportsForm idToEdit={idToEdit} onClose={() => setShowForm(false)} />}

      <MPSharedDeleteModal
        show={showDeleteWarning}
        entityName={getLocalizedString("report", "Report")}
        error={deleteErrorMsg}
        itemName={getDeleteItemName(idToDelete)}
        onHide={() => {
          setDeleteErrorMsg();
          setShowDeleteWarning(false);
        }}
        onDelete={() => deleteReport({ id: idToDelete })}
      />
    </>
  );
};
